<template>
    <div v-if="lastPage > 1"
        class="flex flex-col sm:flex-row justify-between items-center gap-4 py-4 px-4 border-t border-gray-100">
        <!-- Info de pagination -->
        <div class="text-xs text-gray-500">
            Affichage de <span class="font-medium text-gray-700">{{ fromValue }}</span> a <span class="font-medium text-gray-700">{{ to }}</span> sur
            <span class="font-medium text-gray-700">{{ total }}</span> elements
        </div>

        <!-- Controles de pagination -->
        <div class="flex items-center gap-2">
            <!-- Bouton precedent -->
            <button
                type="button"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                @click="onPageChange(currentPage - 1)"
                :disabled="currentPage === 1"
            >
                <i class="fa-solid fa-chevron-left text-xs"></i>
                Precedent
            </button>

            <!-- Numeros de page -->
            <div v-if="showPageNumbers" class="flex items-center gap-1">
                <button
                    v-for="page in displayedPages"
                    :key="page"
                    type="button"
                    @click="onPageChange(page)"
                    :class="[
                        'w-8 h-8 rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center',
                        page === currentPage
                            ? 'bg-primary text-white'
                            : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    ]"
                >
                    {{ page }}
                </button>
            </div>

            <!-- Bouton suivant -->
            <button
                type="button"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                @click="onPageChange(currentPage + 1)"
                :disabled="currentPage >= lastPage"
            >
                Suivant
                <i class="fa-solid fa-chevron-right text-xs"></i>
            </button>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue'

export default {
    name: 'PaginationComponent',
    props: {
        currentPage: {
            type: Number,
            required: true
        },
        perPage: {
            type: Number,
            required: true
        },
        total: {
            type: Number,
            required: true
        },
        to: {
            type: Number,
            required: true
        },
        from: {
            type: Number,
            default: null
        },
        showPageNumbers: {
            type: Boolean,
            default: true
        },
        maxVisiblePages: {
            type: Number,
            default: 5
        }
    },
    emits: ['page-change'],
    setup(props, { emit }) {
        const lastPage = computed(() => {
            return Math.ceil(props.total / props.perPage)
        })

        const fromValue = computed(() => {
            if (props.from !== null && props.from !== undefined) {
                return props.from
            }
            if (props.total === 0) return 0
            return (props.currentPage - 1) * props.perPage + 1
        })

        const displayedPages = computed(() => {
            const pages = []
            const last = lastPage.value
            const current = props.currentPage
            const max = props.maxVisiblePages

            if (last <= max) {
                for (let i = 1; i <= last; i++) {
                    pages.push(i)
                }
            } else {
                if (current <= Math.ceil(max / 2)) {
                    for (let i = 1; i <= max; i++) {
                        pages.push(i)
                    }
                } else if (current >= last - Math.floor(max / 2)) {
                    for (let i = last - max + 1; i <= last; i++) {
                        pages.push(i)
                    }
                } else {
                    const half = Math.floor(max / 2)
                    for (let i = current - half; i <= current + half; i++) {
                        if (i > 0 && i <= last) {
                            pages.push(i)
                        }
                    }

                    while (pages.length < max) {
                        if (pages[0] > 1) {
                            pages.unshift(pages[0] - 1)
                        } else if (pages[pages.length - 1] < last) {
                            pages.push(pages[pages.length - 1] + 1)
                        } else {
                            break
                        }
                    }
                }
            }

            return pages
        })

        const onPageChange = (page) => {
            if (page < 1 || page > lastPage.value) return
            emit('page-change', page)
        }

        return {
            lastPage,
            fromValue,
            displayedPages,
            onPageChange
        }
    }
}
</script>
